import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { isOwner, lastSeen } from "../pages/Account";
import { toast } from "sonner";
import { Trash2, StickyNote, CheckCircle2, RotateCcw } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Panel } from "./Widgets";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

export function NotesPanel({ targetType, targetId, className = "", onChange }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const load = () => api.get("/notes", { params: { target_type: targetType, target_id: targetId } }).then((r) => setNotes(r.data));
  useEffect(() => { load(); }, [targetType, targetId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!text.trim()) return;
    try {
      await api.post("/notes", { target_type: targetType, target_id: targetId, text });
      setText(""); load(); onChange?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/notes/${id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (n) => {
    try {
      await api.patch(`/notes/${n.id}`, { resolved: !n.resolved });
      toast.success(n.resolved ? "Note reopened" : "Marked resolved"); load(); onChange?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Panel title="Private notes" className={className} action={<span className="flex items-center gap-1 text-xs text-slate-500"><StickyNote size={13} />admin only</span>}>
      <div className="p-5 space-y-3" data-testid="notes-panel">
        <Textarea data-testid="note-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Follow-up reminders, deal terms…" className="bg-[#0B0F17] min-h-[70px]" />
        <div className="flex justify-end"><Button data-testid="add-note-button" size="sm" onClick={add} disabled={!text.trim()} className="bg-blue-500 hover:bg-blue-600 text-white">Add note</Button></div>
        {notes.map((n) => (
          <div key={n.id} className={`rounded-md border p-3 ${n.resolved ? "border-slate-800/60 bg-[#0B0F17]/50 opacity-70" : "border-slate-800 bg-[#0B0F17]"}`} data-testid={`note-${n.id}`}>
            <div className="flex justify-between gap-3">
              <p className={`text-sm whitespace-pre-wrap ${n.resolved ? "text-slate-400 line-through" : "text-slate-200"}`}>{n.text}</p>
              <div className="flex items-start gap-2 shrink-0">
                <button data-testid={`resolve-note-${n.id}`} onClick={() => toggle(n)} title={n.resolved ? "Reopen" : "Mark resolved"}
                  className={`flex items-center gap-1 text-[11px] font-mono uppercase transition-colors ${n.resolved ? "text-emerald-400 hover:text-slate-300" : "text-slate-500 hover:text-emerald-400"}`}>
                  {n.resolved ? <><RotateCcw size={13} />Reopen</> : <><CheckCircle2 size={13} />Resolve</>}
                </button>
                {isOwner(user) && <button data-testid={`delete-note-${n.id}`} onClick={() => remove(n.id)} className="text-slate-600 hover:text-rose-400"><Trash2 size={14} /></button>}
              </div>
            </div>
            <div className="text-[11px] font-mono text-slate-500 mt-2">{new Date(n.created_at).toLocaleString()}
              {n.resolved && <span data-testid={`note-resolved-${n.id}`} className="ml-2 text-emerald-500">· resolved {n.resolved_at ? new Date(n.resolved_at).toLocaleDateString() : ""}</span>}</div>
          </div>
        ))}
      </div>
    </Panel>
  );
}
